import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setBearerToken, clearAuth } from '../api/client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8090';

// Single-flight refresh guard: if multiple 401s fire at once, they all await the same promise.
// Prevents refresh-token rotation race where one refresh invalidates the token being used by another.
let _refreshInFlight = null;

// ─── Plusieurs onglets, un seul jeton de rafraîchissement ───
// Le serveur fait TOURNER le jeton de rafraîchissement à chaque usage, avec détection de
// rejeu : présenter un jeton déjà échangé supprime TOUTES les sessions du compte
// (routes/auth.py). Or chaque onglet garde en mémoire le jeton lu à son ouverture. Un onglet
// neuf (/rapports, /studio-ia-beta) le faisait tourner aussitôt ; l'éditeur, resté sur
// l'ancien, déclenchait le rejeu à son propre rafraîchissement — déconnexion partout.
// Deux parades : relire le jeton le plus RÉCENT dans localStorage (le stockage persistant
// est partagé par les onglets), et faire passer les rafraîchissements un par un, d'un
// onglet à l'autre, sous un verrou Web Locks — deux onglets ne présentent jamais le même.
function jetonStocke() {
    try {
        return JSON.parse(localStorage.getItem('labelforge-auth') || 'null')?.state?.refreshToken || null;
    } catch {
        return null;
    }
}

function sousVerrouPartage(travail) {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request('labelforge-rafraichissement', travail);
    }
    return travail();
}

// ─── Mise en forme des refus du serveur ───
// Ces trois fonctions sont exportées et partagées avec les écrans qui parlent aux
// routes /api/auth (UserProfileModal) : un même refus doit se lire pareil partout,
// et on ne veut pas deux formateurs qui divergent.

// `res.json()` était appelé AVANT tout test de `res.ok`, et même sur un 200 : une
// réponse non-JSON (page d'erreur d'un intermédiaire, 502) faisait lever la lecture
// et l'utilisateur recevait une erreur de syntaxe JSON au lieu d'un message.
export const lireJson = async (res) => {
    try { return await res.json(); } catch (e) { return null; }
};

// Libellés lisibles des champs, pour nommer le fautif dans un refus de validation.
const LIBELLES_CHAMPS = {
    email: 'Email',
    username: "Nom d'utilisateur",
    password: 'Mot de passe',
    current_password: 'Mot de passe actuel',
    new_password: 'Nouveau mot de passe',
    avatar_url: 'URL photo de profil',
};

// Les `msg` d'un 422 sont écrits par Pydantic : les contraintes de champ arrivent en
// anglais, et un validateur maison — pourtant en français — est préfixé « Value
// error, ». On nettoie le gabarit technique et on traduit les tournures courantes ;
// un message non reconnu passe tel quel.
const TRADUCTIONS_VALIDATION = [
    [/^String should have at least (\d+) characters?$/, (n) => `doit faire au moins ${n} caractères`],
    [/^String should have at most (\d+) characters?$/, (n) => `ne doit pas dépasser ${n} caractères`],
    [/^Input should be a valid URL/, () => 'doit être une adresse web valide (https://…)'],
    [/^value is not a valid email address/, () => 'doit être une adresse email valide'],
    [/^Input should be a valid string$/, () => 'doit être du texte'],
    [/^Field required$/, () => 'est obligatoire'],
];

const traduireValidation = (msg) => {
    const nu = String(msg).replace(/^(Value error|Assertion failed),\s*/, '');
    for (const [motif, rendu] of TRADUCTIONS_VALIDATION) {
        const trouve = motif.exec(nu);
        if (trouve) return rendu(trouve[1]);
    }
    return nu;
};

// FastAPI renvoie `detail` sous trois formes : une chaîne, un objet, ou — pour un
// 422 de validation — une LISTE d'objets {msg, loc}. On l'affichait tel quel : le
// bandeau montrait « [object Object] », qui n'apprend rien et ne dit pas quel champ
// corriger.
export const formatDetailApi = (detail, secours) => {
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const lignes = detail.map((entree) => {
            if (typeof entree === 'string') return entree;
            const chemin = Array.isArray(entree?.loc) ? entree.loc.filter(p => p !== 'body') : [];
            const champ = chemin.length ? String(chemin[chemin.length - 1]) : null;
            const libelle = champ ? (LIBELLES_CHAMPS[champ] || champ) : null;
            const brut = entree?.msg || entree?.message;
            if (!brut) return libelle ? `${libelle} : valeur refusée` : null;
            const msg = traduireValidation(brut);
            return libelle ? `${libelle} : ${msg}` : msg;
        }).filter(Boolean);
        if (lignes.length) return lignes.join(' · ');
    }
    if (detail && typeof detail === 'object' && (detail.msg || detail.message)) {
        return traduireValidation(detail.msg || detail.message);
    }
    return secours;
};

// Un `fetch` qui échoue (hors ligne, DNS, coupure) lève un TypeError « Failed to
// fetch » : ce texte technique finissait tel quel dans le bandeau rouge.
export const messageUtilisateur = (e, secours) => (
    e instanceof TypeError
        ? 'Le serveur est injoignable. Vérifiez votre connexion, puis réessayez.'
        : (e?.message || secours)
);

const useAuthStoreBase = create(persist((set, get) => ({
    // State
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,

    // ─── Actions ───

    signup: async (email, username, password) => {
        set({ isLoading: true, error: null });
        try {
            const res = await fetch(`${API_URL}/api/auth/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, username, password }),
            });
            const data = await lireJson(res);
            if (!res.ok) {
                throw new Error(formatDetailApi(data?.detail,
                    `L'inscription a échoué (erreur ${res.status}). Réessayez dans un instant ; si cela persiste, contactez l'administrateur.`));
            }
            if (!data) {
                throw new Error("Le serveur a répondu de façon inattendue : impossible de confirmer la création du compte. Essayez de vous connecter avec vos identifiants avant de recommencer.");
            }
            set({ isLoading: false });
            return data;
        } catch (err) {
            set({ isLoading: false, error: messageUtilisateur(err, "L'inscription a échoué. Réessayez dans un instant.") });
            throw err;
        }
    },

    login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await lireJson(res);
            if (!res.ok) {
                throw new Error(formatDetailApi(data?.detail, res.status === 401 || res.status === 403
                    ? 'Email ou mot de passe incorrect. Vérifiez vos identifiants, puis réessayez.'
                    : `La connexion a échoué (erreur ${res.status}). Réessayez dans un instant.`));
            }
            if (!data?.access_token) {
                throw new Error('Le serveur a répondu de façon inattendue : la connexion n\'a pas pu être établie. Réessayez dans un instant.');
            }

            setBearerToken(data.access_token);
            set({
                user: data.user,
                accessToken: data.access_token,
                refreshToken: data.refresh_token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            });
            return data;
        } catch (err) {
            set({ isLoading: false, error: messageUtilisateur(err, 'La connexion a échoué. Réessayez dans un instant.') });
            throw err;
        }
    },

    logout: async () => {
        const { refreshToken } = get();
        try {
            if (refreshToken) {
                await fetch(`${API_URL}/api/auth/logout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken }),
                });
            }
        } catch (e) { /* ignore */ }
        // Clear client.js module-level header — otherwise hasCredentials() keeps returning true
        // and App.jsx stays on the editor instead of redirecting to login.
        clearAuth();
        set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
        });
    },

    refreshAccessToken: async () => {
        // Single-flight: coalesce concurrent callers onto the same refresh promise.
        if (_refreshInFlight) return _refreshInFlight;

        if (!get().refreshToken && !jetonStocke()) {
            // No refresh token at all → truly logged out.
            get().logout();
            return null;
        }

        _refreshInFlight = sousVerrouPartage(async () => {
            // Relu SOUS le verrou : un autre onglet a pu faire tourner le jeton pendant
            // qu'on attendait — c'est alors le sien, le plus récent, qu'il faut présenter.
            const refreshToken = jetonStocke() || get().refreshToken;
            try {
                const res = await fetch(`${API_URL}/api/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken }),
                });
                if (res.status === 401 || res.status === 403) {
                    // Refresh token genuinely invalid/revoked/expired → real logout.
                    get().logout();
                    return null;
                }
                if (!res.ok) {
                    // Transient server error (5xx, 429, etc.) — DO NOT logout.
                    // Return null and let the caller decide (retry, show error, etc.).
                    return null;
                }
                const data = await res.json();
                setBearerToken(data.access_token);
                set({
                    user: data.user,
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                });
                return data.access_token;
            } catch (e) {
                // Network error (offline, DNS, timeout) — DO NOT logout.
                // User may be temporarily offline; preserve session so they can retry.
                return null;
            }
        }).finally(() => {
            _refreshInFlight = null;
        });

        return _refreshInFlight;
    },

    // Fetch with auto-refresh on 401.
    // Throws a typed AuthError when refresh+retry definitively fail, so UI can show
    // a clear message instead of a misleading "endpoint failed" generic error.
    authFetch: async (url, options = {}) => {
        const { accessToken, refreshAccessToken } = get();
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${accessToken}`,
        };

        let res = await fetch(url, { ...options, headers });

        if (res.status === 401) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                headers['Authorization'] = `Bearer ${newToken}`;
                res = await fetch(url, { ...options, headers });
            }
        }

        // Refresh+retry still 401 → disambiguate session-expired vs network blip.
        // refreshAccessToken() logs out only on 401/403 from /api/auth/refresh (= refresh
        // token genuinely invalid). On network errors / 5xx, it returns null without
        // touching auth state — so checking isAuthenticated tells us which case we're in.
        if (res.status === 401) {
            const stillAuthed = get().isAuthenticated;
            const err = new Error(stillAuthed
                ? 'Connexion réseau interrompue. Réessaye dans un instant.'
                : 'Session expirée. Reconnecte-toi.');
            err.code = stillAuthed ? 'NETWORK_AUTH_FAILED' : 'SESSION_EXPIRED';
            throw err;
        }

        return res;
    },

    updateUser: (partial) => set(state => ({
        user: state.user ? { ...state.user, ...partial } : null,
    })),

    clearError: () => set({ error: null }),

}), {
    name: 'labelforge-auth',
    partialize: (state) => ({
        // Only persist non-sensitive data — tokens stay in memory only
        user: state.user,
        refreshToken: state.refreshToken,  // Needed for session restore, server can revoke
        isAuthenticated: state.isAuthenticated,
        // accessToken intentionally NOT persisted (short-lived, refreshed on load)
    }),
    onRehydrateStorage: () => (state) => {
        // On app load, if we have a refreshToken but no accessToken, auto-refresh
        if (state?.refreshToken && state?.isAuthenticated && !state?.accessToken) {
            state.refreshAccessToken();
        }
    },
}));

// `error` appartient à l'ÉCRAN qui l'a produit, pas à l'application : chacun des
// deux écrans d'authentification le vide donc à son arrivée (cf. LoginPage et
// RegisterPage). Une première version enveloppait ce hook pour vider l'erreur au
// démontage de n'importe quel consommateur — cela ajoutait un effet aux cinq
// composants qui lisent ce store, dont ceux qui n'ont rien à voir avec la
// connexion. La portée dépassait de loin le défaut.
export const useAuthStore = useAuthStoreBase;
